import { computed, ref, shallowRef, toValue, triggerRef, watch, type MaybeRefOrGetter } from 'vue';
import { flatten, loadEpubNavigation, type NavigationRow } from '../util/epub-navigation';

type EpubNavigation = Awaited<ReturnType<typeof loadEpubNavigation>>;

const imageRatio = (src: string) =>
  new Promise<number>((resolve, reject) => {
    const image = new Image();
    image.onload = () => (image.naturalHeight ? resolve(image.naturalWidth / image.naturalHeight) : reject());
    image.onerror = reject;
    image.src = src;
  });

// The navigation of the book at `baseUrl` (a signed content base ending in "/"), reloaded when it changes.
const useEpubNavigation = (baseUrl: MaybeRefOrGetter<string>) => {
  const navigation = shallowRef<EpubNavigation>();
  const isLoading = ref(true);
  const hasFailed = ref(false);
  const isPlacingPages = ref(false);
  // Fixed-layout pages come in any shape (square picture books, landscape spreads), so previews take the
  // book's own ratio, read from one of its preview images. Until then: a portrait page.
  const pageRatio = ref(3 / 4);

  watch(
    () => toValue(baseUrl),
    async (url) => {
      navigation.value = undefined;
      isLoading.value = true;
      hasFailed.value = false;
      isPlacingPages.value = false;
      pageRatio.value = 3 / 4;
      let result: EpubNavigation | undefined;
      try {
        result = await loadEpubNavigation(url);
        // The cover can be shaped differently from the pages, so measure the first inner page.
        const sample = result.previews ? (result.rows[1] ?? result.rows[0])?.thumbnail : undefined;
        if (sample) pageRatio.value = await imageRatio(sample).catch(() => pageRatio.value);
      } catch (error) {
        console.error(error);
        if (url === toValue(baseUrl)) hasFailed.value = true;
      }
      if (url !== toValue(baseUrl)) return;
      navigation.value = result;
      isLoading.value = false;
      if (!result?.hasUnplacedPages) return;
      isPlacingPages.value = true;
      await result.placePages();
      if (url !== toValue(baseUrl)) return;
      triggerRef(navigation);
      isPlacingPages.value = false;
    },
    { immediate: true },
  );

  // Every row in reading order, collapsed or not.
  const allRows = computed(() => flatten(navigation.value?.rows ?? []));
  const orderOf = (row: NavigationRow) => allRows.value.indexOf(row);
  const lastInside = (row: NavigationRow) => {
    let last = row;
    while (last.children.length) last = last.children[last.children.length - 1];
    return orderOf(last);
  };
  // The id is the row's path in the tree, "3.1" being the second child of the fourth row.
  const rowById = (id: string) =>
    id
      .split('.')
      .reduce<
        NavigationRow | undefined
      >((row, index) => (row ? row.children : (navigation.value?.rows ?? []))[Number(index)], undefined);
  const parentOf = (row: NavigationRow) =>
    row.depth > 0 ? rowById(row.id.split('.').slice(0, -1).join('.')) : undefined;

  return {
    navigation,
    isLoading,
    hasFailed,
    isPlacingPages,
    pageRatio,
    allRows,
    orderOf,
    lastInside,
    rowById,
    parentOf,
  };
};

export default useEpubNavigation;
